import api from "../../../api/Api";
import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import backendToInputDate from "../../../utils/backendToInputDate";

import { RequiredLabel } from "../../../components/RequiredLabel/RequiredLabel";
// import { ToastContainer, toast } from "react-toastify";

const AssetForm = forwardRef(({ initialData = {}, onSubmit, isEdit = false }, ref) => {
  const [assetTypes, setAssetTypes] = useState([]);
  const [dynamicAttributes, setDynamicAttributes] = useState([]);
  const [assetStatuses, setAssetStatuses] = useState([]);
  const [locations, setLocations] = useState([]);
    useEffect(() => {
  api
    .get("/api/employees/location")
    .then(res => {
      console.log("API response:", res);

      if (res?.data?.status) {
        setLocations(res.data.response || []);
      }
    })
    .catch(err => {
      console.error("Failed to fetch locations", err);
    });
}, []);
  // Build initial values
  const buildInitialValues = () => {
    const baseValues = {
      assetName: initialData.assetName || "",
      serialNumber: initialData.serialNumber || "",
      assetStatus: initialData.assetStatus || "UNASSIGNED",
      purchaseCost: initialData.purchaseCost || "",
      purchaseDate: backendToInputDate(initialData.purchaseDate) || "",
      notes: initialData.notes || "",
      assetTypeIdenty: initialData?.assetType?.identy || "",
      assignedEmployeeName: initialData?.assignedEmployee?.employeeName || "",
      assignedEmployeeId: initialData?.assignedEmployee?.employeeId || "",
      ownershipType: initialData.ownershipType || "OWNED",
      location: initialData.location || "",
    };

    // Add dynamic attributes as separate fields
    const attributeValues = {};
    initialData?.attributes?.forEach(attr => {
      attributeValues[`attr_${attr.identy}`] = attr.attributeValue || "";
    });

    return { ...baseValues, ...attributeValues };
  };

  // Build validation schema dynamically
  const buildValidationSchema = (attrs) => {
    const baseSchema = {
      assetName: Yup.string().required("Asset Name is required"),
      assetTypeIdenty: Yup.string().required("Asset Type is required"),
      assetStatus: Yup.string().required("Asset Status is required"),
      purchaseDate: Yup.string().required("Purchase Date is required"),
      ownershipType: Yup.string().required("Ownership Type is required"),
      purchaseCost: Yup.number()
        .required("Purchase Cost is required")
        .positive("Purchase Cost must be greater than 0")
        .typeError("Purchase Cost must be a number"),
      location: Yup.string().required("Location is required"),
    };

    // Add dynamic attribute validations
    attrs.forEach(attr => {
      if (attr) {
        baseSchema[`attr_${attr.attributeIdenty}`] = Yup.string().required(`${attr.attributeLabel} is required`);
      }
    });

    return Yup.object().shape(baseSchema);
  };

  const formik = useFormik({
    initialValues: buildInitialValues(),
    validationSchema: buildValidationSchema(dynamicAttributes),
    enableReinitialize: true,
    onSubmit: (values) => {
      // Transform formik values back to expected API format
      const submitData = {
        assetName: values.assetName,
        serialNumber: values.serialNumber,
        assetStatus: values.assetStatus,
        purchaseCost: values.purchaseCost,
        purchaseDate: values.purchaseDate,
        notes: values.notes,
        ownershipType: values.ownershipType,
        assetTypeIdenty: values.assetTypeIdenty,
        assignedEmployeeName: values.assignedEmployeeName,
        assignedEmployeeId: values.assignedEmployeeId,
        location: values.location,
        attributes: dynamicAttributes.map(attr => ({
          attributeIdenty: attr.attributeIdenty,
          attributeValue: values[`attr_${attr.attributeIdenty}`] || "",
        })),
      };
      onSubmit(submitData);
    },
  });

  // Fetch asset types and statuses on mount
  useEffect(() => {
    Promise.all([fetchAssetTypes(), fetchAssetStatuses()]);
  }, []);

  // Fetch dynamic attributes when asset type changes
  useEffect(() => {
    if (!formik.values.assetTypeIdenty) {
      setDynamicAttributes([]);
      return;
    }
    fetchAttributesByType(formik.values.assetTypeIdenty);
  }, [formik.values.assetTypeIdenty]);

  // Fetch asset types
  const fetchAssetTypes = () => {
    return api
      .get("/api/assets-type/drop")
      .then((res) => {
        if (res.data?.response) setAssetTypes(res.data.response);
      })
      .catch((err) => {
        console.error("Error fetching asset types:", err);
      });
  };

  // Fetch asset statuses
  const fetchAssetStatuses = () => {
    return api
      .get("/api/assets/status")
      .then((res) => {
        setAssetStatuses(res.data?.response);
      })
      .catch((err) => {
        console.error("Error fetching asset statuses:", err);
      });
  };

  // Fetch dynamic attributes for selected asset type
  const fetchAttributesByType = (assetTypeIdenty) => {
    api
      .get(`/api/attribute/assets-type/${assetTypeIdenty}`)
      .then((res) => {
        const attrDefs = res.data?.response?.attributes || [];
        setDynamicAttributes(attrDefs);

        // Prepare new values object
        const updatedValues = { ...formik.values };

        // Clear previous dynamic attributes only in ADD mode
        if (!isEdit) {
          Object.keys(updatedValues).forEach((key) => {
            if (key.startsWith("attr_")) updatedValues[key] = "";
          });
        }

        // Populate new attributes
        attrDefs.forEach((attrDef) => {
          const fieldName = `attr_${attrDef.attributeIdenty}`;
          // In edit mode, use existing value if available, else empty
          const existingValue = initialData?.attributes?.find(a => a.attributeIdenty === attrDef.attributeIdenty)?.attributeValue;
          updatedValues[fieldName] = existingValue || updatedValues[fieldName] || "";
        });

        formik.setValues(updatedValues);
      })
      .catch((err) => {
        console.error("Error fetching attributes:", err);
        // On error, clear dynamic attributes in ADD mode
        setDynamicAttributes([]);
        if (!isEdit) {
          const clearedValues = { ...formik.values };
          Object.keys(clearedValues).forEach((key) => {
            if (key.startsWith("attr_")) clearedValues[key] = "";
          });
          formik.setValues(clearedValues);
        }
      });
  };


  const handleAssetTypeChange = (e) => {
    formik.handleChange(e);
  };

  useEffect(() => {
    Promise.all([fetchAssetTypes(), fetchAssetStatuses()]);
  }, []);

  // Expose submit function to parent via ref
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      // Validate all fields
      formik.validateForm().then(errors => {
        if (Object.keys(errors).length === 0) {
          // No errors, submit the form
          formik.handleSubmit();
        } else {
          // Show first error
          const firstError = Object.values(errors)[0];
          // toast.error(firstError);

          // Mark all fields as touched to show errors
          const touchedFields = {};
          Object.keys(formik.initialValues).forEach(key => {
            touchedFields[key] = true;
          });
          formik.setTouched(touchedFields);
        }
      });
    },
  }));

  return (
    <form className="p-4 border rounded bg-white">
      <div className="row">
        <div className="col mb-2">
          <RequiredLabel label="Asset Name" htmlFor="assetName" />
          <input
            type="text"
            name="assetName"
            id="assetName"
            value={formik.values.assetName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`form-control ${formik.touched.assetName && formik.errors.assetName ? "is-invalid" : ""
              }`}
          />
          {formik.touched.assetName && formik.errors.assetName && (
            <div className="invalid-feedback">{formik.errors.assetName}</div>
          )}
        </div>

        <div className="col mb-3">
          <RequiredLabel label="Asset Type" htmlFor="assetTypeIdenty" />
          <select
            name="assetTypeIdenty"
            id="assetTypeIdenty"
            value={formik.values.assetTypeIdenty}
            onChange={handleAssetTypeChange}
            onBlur={formik.handleBlur}
            disabled={isEdit}
            className={`form-select ${formik.touched.assetTypeIdenty && formik.errors.assetTypeIdenty ? "is-invalid" : ""
              }`}
          >
            <option value="">--- Select Asset Type ---</option>
            {assetTypes.map((type) => (
              <option key={type.identy} value={type.identy} className="text-capitalize">
                {type.assetTypeName.toLowerCase()}
              </option>
            ))}
          </select>
          {formik.touched.assetTypeIdenty && formik.errors.assetTypeIdenty && (
            <div className="invalid-feedback">{formik.errors.assetTypeIdenty}</div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col mb-3">
          <label htmlFor="serialNumber" className="form-label">Serial Number</label>
          <input
            type="text"
            name="serialNumber"
            id="serialNumber"
            value={formik.values.serialNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="form-control"
          />
        </div>

        <div className="col mb-3">
          <RequiredLabel label="Asset Status" htmlFor="assetStatus" />
          <select
            name="assetStatus"
            id="assetStatus"
            value={formik.values.assetStatus}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={isEdit && (formik.values.assetStatus === "ASSIGNED" || formik.values.assetStatus === "ASSIGNED_WITH_ISSUE")} required={isEdit}
            className={`form-select ${formik.touched.assetStatus && formik.errors.assetStatus ? "is-invalid" : ""
              }`}
          >
            {assetStatuses.map((status) => (
              <option
                key={status}
                value={status}
                disabled={status === "ASSIGNED" || status === "ASSIGNED_WITH_ISSUE"}
              >
                {status}
              </option>
            ))}
          </select>
          {formik.touched.assetStatus && formik.errors.assetStatus && (
            <div className="invalid-feedback">{formik.errors.assetStatus}</div>
          )}
        </div>

        <div className="col mb-3">
          <RequiredLabel label="Ownership Type" htmlFor="ownershipType" />
          <select
            name="ownershipType"
            id="ownershipType"
            value={formik.values.ownershipType}
            onChange={formik.handleChange}
            disabled={isEdit}
            onBlur={formik.handleBlur}
            className={`form-select ${formik.touched.ownershipType && formik.errors.ownershipType ? "is-invalid" : ""}`}
          >
            <option value="">--- Select Ownership Type ---</option>
            <option value="OWNED">Owned</option>
            <option value="RENTAL">Rental</option>
          </select>
          {formik.touched.ownershipType && formik.errors.ownershipType && (
            <div className="invalid-feedback">{formik.errors.ownershipType}</div>
          )}
        </div>

      </div>

      <div className="row">
        <div className="col mb-3">
          <RequiredLabel label="Purchase Date" htmlFor="purchaseDate" />
          <input
            type="date"
            name="purchaseDate"
            id="purchaseDate"
            value={formik.values.purchaseDate}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`form-control ${formik.touched.purchaseDate && formik.errors.purchaseDate ? "is-invalid" : ""
              }`}
          />
          {formik.touched.purchaseDate && formik.errors.purchaseDate && (
            <div className="invalid-feedback">{formik.errors.purchaseDate}</div>
          )}
        </div>
        <div className="col mb-3">
          <RequiredLabel label="Purchase Cost" htmlFor="purchaseCost" />
          <input
            type="number"
            name="purchaseCost"
            id="purchaseCost"
            value={formik.values.purchaseCost}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            onWheel={(e) => e.target.blur()}
            className={`form-control ${formik.touched.purchaseCost && formik.errors.purchaseCost ? "is-invalid" : ""
              }`}
            min="0"
            step="0.01"
          />
          {formik.touched.purchaseCost && formik.errors.purchaseCost && (
            <div className="invalid-feedback">{formik.errors.purchaseCost}</div>
          )}
        </div>
         <div className="col mb-3">
          <RequiredLabel label="Location" htmlFor="location" />
          <select
            name="location"
            id="location"
            value={formik.values.location}
            onChange={formik.handleChange}
            
            onBlur={formik.handleBlur}
            className={`form-select ${formik.touched.location && formik.errors.location ? "is-invalid" : ""}`}
          >
            <option value="">--- Select Location ---</option>
             {locations.map(loc => (
                  <option
                    key={loc.locationIdnty}
                    value={loc.locationName} 
                  >
                    {loc.locationName}
                  </option>
            ))}
          </select>
          {formik.touched.ownershipType && formik.errors.ownershipType && (
            <div className="invalid-feedback">{formik.errors.ownershipType}</div>
          )}
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label">Notes</label>
        <textarea
          name="notes"
          id="notes"
          value={formik.values.notes}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          className="form-control"
          rows="3"
        />
      </div>

      {/* Dynamic attributes */}
      {dynamicAttributes.length > 0 && (
        <>
          <hr />
          <h5>Additional Attributes</h5>
          {dynamicAttributes.map((attrDef) => {
            const fieldName = `attr_${attrDef.attributeIdenty}`;
            const isTouched = formik.touched[fieldName];
            const error = formik.errors[fieldName];

            return (
              <div className="mb-3" key={attrDef.attributeIdenty}>
                <RequiredLabel label={attrDef.attributeLabel} htmlFor={attrDef.attributeLabel} />

                <input
                  type="text"
                  name={fieldName}
                  className={`form-control ${isTouched && error ? "is-invalid" : ""
                    }`}
                  value={formik.values[fieldName] || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {isTouched && error && (
                  <div className="invalid-feedback">{error}</div>
                )}
                {attrDef.required && !error && (
                  <div className="  -text text-muted">This field is required</div>
                )}
              </div>
            );
          })}
        </>
      )}
    </form>
  );
});

export default AssetForm;